import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Clock3,
  Copy,
  Landmark,
  PackageCheck,
  QrCode,
  Receipt,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import PaymentTutorial from "../components/PaymentTutorial";
import QrisPaymentQr from "../components/QrisPaymentQr";
import SEOHead from "../components/SEOHead";
import { checkoutApi } from "../lib/api";
import { formatDate, formatRupiah } from "../lib/format";

const PaymentPage = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const data = await checkoutApi.getOrder(invoiceId);
      if (data?.payment_status === "dibayar") {
        navigate(`/sukses/${invoiceId}`, { replace: true });
        return;
      }
      setOrder(data);
    } catch {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, navigate]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Disalin ke clipboard");
  };

  const handleCheckStatus = async () => {
    const before = order?.payment_status;
    await loadOrder();
    if (before !== "dibayar") {
      toast.info("Status pembayaran diperbarui dari server.");
    }
  };

  if (loading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="text-stone-600">Memuat instruksi pembayaran...</p>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="mb-3 font-display text-3xl font-semibold text-stone-900">
          Pesanan tidak ditemukan
        </h1>
        <p className="mb-6 text-stone-600">
          Invoice <span className="font-medium">{invoiceId}</span> tidak tersedia di database.
        </p>
        <Link
          to="/"
          className="inline-flex h-12 items-center rounded-xl bg-emerald-600 px-5 text-white hover:bg-emerald-700"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Kembali ke Beranda
        </Link>
      </main>
    );
  }

  const isQris = order.payment_method === "qris";
  const isCod = order.payment_provider === "cod";
  const isPaid = order.payment_status === "dibayar";
  const totalPayment = order.total_payment || order.total;

  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <SEOHead
        title="Instruksi Pembayaran"
        description="Halaman instruksi pembayaran pesanan Teras Alami."
        path={invoiceId ? `/payment/${invoiceId}` : "/payment"}
        noIndex
      />

      <Link to="/" className="mb-6 inline-flex items-center text-stone-600 hover:text-stone-900">
        <ArrowLeft className="mr-2 h-4 w-4" /> Kembali ke Beranda
      </Link>

      <div className="mb-2 flex items-center gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-amber-100 bg-amber-50 text-amber-700">
          <Clock3 className="h-5 w-5" />
        </span>
        <Badge className="rounded-full border-amber-200 bg-amber-100 px-3 py-1 text-amber-700">
          {isPaid ? "Pembayaran Terkonfirmasi" : "Menunggu Pembayaran"}
        </Badge>
      </div>

      <h1 className="font-display text-3xl font-semibold text-stone-900 sm:text-4xl">
        Selesaikan Pembayaran
      </h1>
      <p className="mt-2 text-lg text-stone-600">
        Gunakan instruksi pembayaran dari gateway. Status akan otomatis berubah setelah callback Pakasir diterima.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-5">
        <section className="space-y-6 lg:col-span-3">
          <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="mb-5 flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                <Receipt className="h-5 w-5" />
              </span>
              <h3 className="font-display text-2xl font-semibold text-stone-900">Detail Pesanan</h3>
            </div>
            <dl className="grid grid-cols-1 gap-4 text-base sm:grid-cols-2">
              <Info label="No. Invoice">
                <span className="flex items-center gap-2">
                  {order.invoice}
                  <button type="button" onClick={() => copy(order.invoice)} className="text-stone-400 hover:text-stone-700">
                    <Copy className="h-4 w-4" />
                  </button>
                </span>
              </Info>
              <Info label="Tanggal">{formatDate(order.created_at)}</Info>
              <Info label="Nama Pembeli">{order.customer.full_name}</Info>
              <Info label="No. HP/WA">{order.customer.phone}</Info>
              <Info label="Produk">
                <ul className="space-y-1">
                  {(order.items || []).map((item) => (
                    <li key={item.id}>
                      {item.product_name_snapshot} x {item.quantity}
                      <span className="ml-2 text-sm text-stone-500">({formatRupiah(item.subtotal)})</span>
                    </li>
                  ))}
                </ul>
              </Info>
              <Info label="Kurir">
                {order.courier_name} - {order.courier_service}
                {order.courier_etd ? ` - ${order.courier_etd}` : ""}
              </Info>
              <Info label="Metode Bayar">{order.payment_method_name || order.payment_method}</Info>
              <Info label="Total Bayar">
                <span className="font-display text-2xl font-semibold text-emerald-700">
                  {formatRupiah(totalPayment)}
                </span>
              </Info>
            </dl>
          </div>

          {!isQris && !isCod && (
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="mb-4 flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                  <Landmark className="h-5 w-5" />
                </span>
                <h3 className="font-display text-xl font-semibold text-stone-900">Virtual Account</h3>
              </div>
              <div className="grid gap-3">
                <PaymentBox label="Channel" value={order.payment_method_name || order.payment_method} icon />
                <PaymentBox label="Nomor Pembayaran / VA" value={order.payment_number || "-"} onCopy={() => copy(order.payment_number)} />
                <PaymentBox label="Berlaku Hingga" value={order.payment_expired_at ? formatDate(order.payment_expired_at) : "-"} />
                <PaymentBox label="Nominal Transfer" value={formatRupiah(totalPayment)} onCopy={() => copy(String(totalPayment))} highlight />
              </div>
            </div>
          )}

          {isQris && (
            <div className="rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm sm:p-8">
              <div className="mb-4 flex items-center justify-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-700">
                  <QrCode className="h-5 w-5" />
                </span>
                <h3 className="font-display text-xl font-semibold text-stone-900">Scan QRIS Berikut</h3>
              </div>
              <QrisPaymentQr qrString={order.payment_number || ""} alt={`QRIS ${order.invoice}`} />
              <p className="mt-4 text-stone-600">
                Nominal: <span className="font-semibold text-emerald-700">{formatRupiah(totalPayment)}</span>
              </p>
            </div>
          )}

          {isCod && (
            <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
              <h3 className="mb-2 font-display text-xl font-semibold text-stone-900">Bayar di Tempat (COD)</h3>
              <p className="text-stone-600">
                Siapkan total {formatRupiah(order.total)} untuk dibayarkan kepada kurir ketika paket sampai.
              </p>
            </div>
          )}

          <Button
            type="button"
            onClick={handleCheckStatus}
            variant="outline"
            className="h-14 w-full rounded-xl border-stone-200 text-base"
          >
            <Clock3 className="mr-2 h-5 w-5" /> Cek Status Pembayaran
          </Button>
        </section>

        <aside className="lg:col-span-2">
          <PaymentTutorial type={isQris ? "qris" : isCod ? "cod" : "bank"} />
        </aside>
      </div>
    </main>
  );
};

const Info = ({ label, children }) => (
  <div>
    <dt className="text-sm text-stone-500">{label}</dt>
    <dd className="font-medium text-stone-900">{children}</dd>
  </div>
);

const PaymentBox = ({ label, value, onCopy, highlight = false, icon = false }) => (
  <div className={`flex items-center justify-between rounded-xl border p-4 ${highlight ? "border-emerald-200 bg-emerald-50" : "border-stone-200 bg-stone-50"}`}>
    <div>
      <p className={`text-sm ${highlight ? "text-emerald-700" : "text-stone-500"}`}>{label}</p>
      <p className={`font-display text-lg font-semibold ${highlight ? "text-emerald-800" : "text-stone-900"}`}>{value}</p>
    </div>
    {icon && <PackageCheck className="h-5 w-5 text-emerald-600" />}
    {onCopy && (
      <Button type="button" variant="outline" onClick={onCopy} className="h-11 rounded-xl">
        <Copy className="mr-2 h-4 w-4" /> Salin
      </Button>
    )}
  </div>
);

export default PaymentPage;
